import { Icon } from '@fluentui/react/lib/Icon';
import { observer } from 'mobx-react-lite';
import { ControlIcons } from 'owa-control-icons';
import { formatShortMonthDay, formatShortMonthDayYear, isSameYear, OwaDate } from 'owa-datetime';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { observableNow, isToday } from 'owa-observable-datetime';
import type { Todo } from 'owa-todo-types';
import { isOverdue } from 'owa-todo-utils';
import LinkedEmailEntity from './LinkedEmailEntity';
import * as React from 'react';
import { todayDueDateLabel } from './TodoListItemInfo.locstring.json';
import loc from 'owa-localize';

const styles = require('./TodoListItemInfo.scss');
const classNames = require('classnames/bind').bind(styles);

export interface TodoListItemInfoProps {
    todo: Todo;
    todoListInfo?: TodoListInfo;
    showReminderLabel: boolean;
    showEmailLabel: boolean;
    onClickEmail: (emailId: string) => void;
}
export interface TodoListInfo {
    listName: string;
    icon?: string;
}

export default observer(function TodoListItemInfo(props: TodoListItemInfoProps) {
    const { todo, todoListInfo, onClickEmail, showReminderLabel, showEmailLabel } = props;

    return (
        <div className={styles.taskItemInfo}>
            {todoListInfo && <TodoListInfo listInfo={todoListInfo} />}
            <TodoDueDate todo={todo} />
            <TodoReminder todo={todo} showReminderLabel={showReminderLabel} />
            {todo.LinkedEmail && (
                <LinkedEmailEntity
                    emailId={todo.LinkedEmail.Id}
                    onClick={onClickEmail}
                    style={styles.emailRoot}
                    iconStyle={styles.emailCharmIcon}
                    showEmailLabel={showEmailLabel}
                />
            )}
        </div>
    );
});

const TodoDueDate = observer(function TodoDueDate(props: { todo: Todo }) {
    const { DueDateTime } = props.todo;

    if (!DueDateTime) {
        return null;
    }

    const now = observableNow();
    const overdue = isOverdue(DueDateTime, now);
    const overdueDark = overdue && getIsDarkTheme();
    return (
        <span
            className={classNames(styles.dueDate, {
                [styles.overdue]: overdue,
                [styles.overdueDark]: overdueDark,
            })}>
            <Icon iconName={ControlIcons.Event} className={styles.icon} />
            <span className={styles.taskLabel}>
                {isToday(DueDateTime)
                    ? loc(todayDueDateLabel)
                    : getFormattedDateTime(DueDateTime, now)}
            </span>
        </span>
    );
});

const TodoListInfo = observer(function TodoListInfo(props: { listInfo: TodoListInfo }) {
    const { listInfo } = props;
    return (
        <span className={styles.listName}>
            {listInfo.icon && <Icon iconName={listInfo.icon} className={styles.icon} />}
            <span className={styles.taskLabel}>{listInfo.listName}</span>
        </span>
    );
});

const TodoReminder = observer(function TodoReminder(props: {
    todo: Todo;
    showReminderLabel: boolean;
}) {
    const { IsReminderOn, ReminderDateTime } = props.todo;

    if (!IsReminderOn || !ReminderDateTime) {
        return null;
    }

    return (
        <span className={styles.reminder}>
            <Icon iconName={ControlIcons.AlarmClock} className={styles.icon} />
            {props.showReminderLabel && (
                <span>{getFormattedDateTime(ReminderDateTime, observableNow())}</span>
            )}
        </span>
    );
});

function getFormattedDateTime(date: OwaDate, now: OwaDate): string {
    return isSameYear(date, now) ? formatShortMonthDay(date) : formatShortMonthDayYear(date);
}
